import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addProgramRevision,
  ControlType,
  FoodControlPointInstance,
  InstanceStatus,
  ProgramRevision,
  REVISION_CONTROLS,
  updateProgramRevision,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ControlsOptions } from "..";

interface RevisionModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  initialData?: ProgramRevision;
}

export function RevisionModal({
  foodControlPointInstance,
  initialData,
  ...props
}: RevisionModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [optionalNote, setOptionalNote] = useState<string>();
  const [controlsState, setControlsState] = useState<
    Record<ControlType, { title: string; value: number }>
  >(
    REVISION_CONTROLS(t, initialData).reduce(
      (acc, control) => {
        acc[control.type] = { title: control.title, value: control.value };
        return acc;
      },
      {} as Record<ControlType, { title: string; value: number }>,
    ),
  );

  const isSolveRequired = useMemo(
    () =>
      Object.values(controlsState).some(
        (control) => control.value === 3 || control.value === 2,
      ),
    [controlsState],
  );

  const programRevisionSchema = useMemo(() => {
    return z.object({
      note: z
        .string()
        .optional()
        .refine(
          (value) => {
            if (!isSolveRequired) return true;
            return !!value;
          },
          {
            message: t("food.Remarks are mandatory"),
          },
        ),
    });
  }, [isSolveRequired, t]);

  type ProgramRevisionSchema = z.infer<typeof programRevisionSchema>;
  const {
    handleSubmit,
    register,
    formState: state,
    reset,
  } = useForm<ProgramRevisionSchema>({
    resolver: zodResolver(programRevisionSchema),
  });

  useEffect(() => {
    if (!isSolveRequired) {
      reset();
    }
  }, [isSolveRequired, reset]);

  const updateMutation = useMutation({
    mutationFn: updateProgramRevision,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Program Revision has been saved"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(
        t("food.An error occurred while saving the program revision"),
      );
    },
  });

  const mutation = useMutation({
    mutationFn: addProgramRevision,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Program Revision has been saved"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(
        t("food.An error occurred while saving the program revision"),
      );
    },
  });
  const handleSaveClick = useCallback(
    (data: ProgramRevisionSchema) => {
      if (!user?.id) return;
      const isCompeletedWithErrors = isSolveRequired;
      const baseData = {
        companyId: foodControlPointInstance.companyId,
        companyLocationId: foodControlPointInstance.companyLocationId,
        controlPointInstanceId: foodControlPointInstance.id,
        status: isCompeletedWithErrors
          ? InstanceStatus.completedWithErrors
          : InstanceStatus.completed,
        reason: optionalNote,
        remark: data.note,
        registeredBy: user?.id,
      };

      const controlData = Object.keys(controlsState).reduce(
        (acc, key) => {
          const controlKey = key as ControlType;
          acc[controlKey] = controlsState[controlKey].value;
          return acc;
        },
        {} as Record<ControlType, number>,
      );

      const payload = {
        ...baseData,
        ...controlData,
        foodControlPointInstance,
      };

      if (initialData) {
        updateMutation.mutate(payload);
        return;
      }

      mutation.mutate(payload);
    },
    [
      user?.id,
      isSolveRequired,
      foodControlPointInstance,
      optionalNote,
      controlsState,
      initialData,
      mutation,
      updateMutation,
    ],
  );

  useEffect(() => {
    if (initialData) {
      const { remark, reason } = JSON.parse(initialData.note || "{}");

      setOptionalNote(remark);

      reset({
        note: reason,
      });
    }
  }, [initialData, reset]);

  return (
    <Modal
      {...props}
      title={t("food.Revision of Program")}
      actionButtons={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmit(handleSaveClick)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
    >
      <form>
        <ControlsOptions
          setControlsState={setControlsState}
          controlsState={controlsState}
        />

        {isSolveRequired && (
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Corrective Action")} required>
              <Input {...register("note")} />
            </Label>
            <ErrorMessage message={state.errors.note?.message} />
          </div>
        )}

        <Label title={t("food.Remarks")}>
          <Input
            value={optionalNote}
            onChange={(e) => setOptionalNote(e.target.value)}
          />
        </Label>
      </form>
    </Modal>
  );
}
